import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { addPlayer, addPlayerToMatch } from "app/thunks/playersAPI";
import { Player } from "interfaces/Players";
import { handleTableDataSorting, SortProps } from "utils/table/table";

import { RootState } from "../../app/store";

export interface State {
  list: Player[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isFetching: boolean;
  error: any;
  selectedPlayers: any;
}

const initialState: State = {
  list: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  isFetching: false,
  error: undefined,
  selectedPlayers: {},
};

export const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {
    sortPlayers: (state, action: PayloadAction<SortProps>) => {
      const { sortBy, sortOrder } = action.payload;
      if (state.list.length > 0 && sortBy && sortOrder) {
        const sorted = [...state.list];
        sorted.sort(
          handleTableDataSorting<Player>({
            sortBy,
            sortOrder,
            dateProperties: ["createdAt"],
          }),
        );
        state.list = sorted;
      }
    },
    resetFlags: (state) => {
      state.isSuccess = false;
      state.isLoading = false;
      state.isError = false;
      state.isFetching = false;
      state.selectedPlayers = {};
    },
    resetPlayersSelected: (state) => {
      state.selectedPlayers = {};
    },
    removeSelectedPlayer: (state, action) => {
      const selectedPlayers = {
        ...state.selectedPlayers,
      };
      delete selectedPlayers[action.payload];
      state.selectedPlayers = selectedPlayers;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(addPlayer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addPlayer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(addPlayer.fulfilled, (state, action: PayloadAction<Player>) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.list.push(action.payload);
      })
      .addCase(addPlayerToMatch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addPlayerToMatch.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(addPlayerToMatch.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.selectedPlayers = {
          ...state.selectedPlayers,
          [action.payload.containerId]: action.payload.playerId,
        };
        // TO.DO define how we will update the corresponding match-container when action is fullfilled
      });
  },
});

export const {
  sortPlayers,
  resetFlags,
  resetPlayersSelected,
  removeSelectedPlayer,
} = playersSlice.actions;

// ------ Selectors ------
export const getPlayers = (state: RootState) => ({
  players: state.players.list,
  isFetching: state.players.isFetching,
});

export const getPlayerActionState = (state: RootState) => ({
  isLoading: state.players.isLoading,
  isSuccess: state.players.isSuccess,
  isError: state.players.isError,
  error: state.players.error,
});

export const getSelectedPlayers = (state: RootState) => ({
  selectedPlayers: state.players.selectedPlayers,
});

export default playersSlice.reducer;
